import { Check, ChevronDown, LayoutDashboard, LogIn, Menu, Pencil, Plus, X } from "lucide-react";
import * as LucideIcons from "lucide-react";
import IconPickerPopover from "../../../custom-pages/IconPickerPopover";
import { motion, AnimatePresence } from "framer-motion";
import { BeamButton } from "../ui/beam-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Dispatch, ReactNode, SetStateAction, useEffect, useRef, useState } from "react";
import { Link as ReactRouterLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import { getCurrentUserRole, useQuery } from "wasp/client/operations";
import { Link as WaspRouterLink, routes } from "wasp/client/router";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../../client/components/ui/sheet";
import { throttleWithTrailingInvocation } from "../../../shared/utils";
import { UserDropdown } from "../../../user/UserDropdown";
import { UserMenuItems } from "../../../user/UserMenuItems";
import { useIsLandingPage } from "../../hooks/useIsLandingPage";
import { useIsMarketingPage } from "../../hooks/useIsMarketingPage";
import { cn } from "../../utils";
import { handleAnchorClick } from "../../utils/scrollToAnchor";
import DarkModeSwitcher from "../DarkModeSwitcher";
import AnalyzeMyTradingButton from "../../../edgy/AnalyzeMyTradingButton";
import EdgyDrawer from "../../../edgy/EdgyDrawer";
import LanguageSwitcher from "../LanguageSwitcher";
import { NotificationBell } from "../../../mentor/NotificationBell";
import useTranslation from "../../hooks/useTranslation";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { getMarketingMegaMenus } from "./constants";

export interface NavigationItem {
  name: string;
  to: string;
  icon?: React.ComponentType<{ className?: string; size?: string | number }>;
}

export interface CustomPageNavItem {
  id: string;
  name: string;
  icon: string;
  to: string;
  onRename: (newName: string) => Promise<void>;
  onChangeIcon: (icon: string) => Promise<void>;
  onDelete: () => Promise<void>;
}

export interface CustomPagesActions {
  items: CustomPageNavItem[];
  onCreatePage: (name: string, icon: string) => Promise<void>;
}

export default function NavBar({
  navigationItems,
  customPages,
}: {
  navigationItems: NavigationItem[];
  customPages?: CustomPagesActions;
}) {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const isMarketingPage = useIsMarketingPage();

  useEffect(() => {
    const throttledHandler = throttleWithTrailingInvocation(() => {
      setIsScrolled(window.scrollY > 0);
    }, 50);

    window.addEventListener("scroll", throttledHandler);

    return () => {
      window.removeEventListener("scroll", throttledHandler);
      throttledHandler.cancel();
    };
  }, []);

  return (
    <>

      <header
        className={cn(
          "sticky top-0 z-50 transition-all duration-300",
          isScrolled && "top-4",
        )}
      >
        <div
          className={cn("transition-all duration-300", {
            "bg-background/90 border-border mx-4 rounded-full border pr-2 shadow-lg backdrop-blur-lg md:mx-20 lg:pr-0":
              isScrolled,
            "bg-background/80 border-border mx-0 border-b backdrop-blur-lg":
              !isScrolled,
          })}
        >
          <nav
            className={cn(
              "relative flex items-center transition-all duration-300",
              {
                "p-3 lg:px-6": isScrolled,
                "p-5 lg:px-8": !isScrolled,
              },
            )}
            aria-label="Global"
          >
            {/* Logo à gauche */}
            <div className="flex flex-1 items-center gap-2">
              <NavBarLogo isScrolled={isScrolled} />
              {/* Sur pages marketing : liens directement après le logo */}
              {isMarketingPage && (
                <ul className="hidden items-center gap-1 lg:flex">
                  <MarketingDesktopNav />
                </ul>
              )}
            </div>

            {/* Sur app : liens centrés absolument */}
            {!isMarketingPage && (
              <ul className="hidden items-center gap-1 lg:flex absolute left-1/2 -translate-x-1/2">
                <NavigationItems navigationItems={navigationItems} navigate={navigate} customPages={customPages} />
              </ul>
            )}

            {/* Éléments à droite */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <EdgyNavAction />
              <NavBarMobileMenu
                isScrolled={isScrolled}
                navigationItems={navigationItems}
                navigate={navigate}
              />
              <NavBarDesktopUserDropdown isScrolled={isScrolled} />
            </div>
          </nav>
        </div>
      </header>
    </>
  );
}

/**
 * Edgy entry point in the global header — the showcase feature, so it lives where it
 * is visible on every app page. Hidden for logged-out visitors and on marketing pages,
 * where it would have nothing to analyse.
 */
function EdgyNavAction() {
  const { data: user } = useAuth();
  const isLandingPage = useIsLandingPage();
  const isMarketingPage = useIsMarketingPage();
  const [open, setOpen] = useState(false);

  if (!user || isLandingPage || isMarketingPage) return null;

  return (
    <>
      <AnalyzeMyTradingButton onClick={() => setOpen(true)} className="hidden sm:inline-flex" />
      <AnalyzeMyTradingButton onClick={() => setOpen(true)} compact className="sm:hidden" />
      <EdgyDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function NavBarDesktopUserDropdown({ isScrolled }: { isScrolled: boolean }) {
  const { data: user, isLoading: isUserLoading } = useAuth();
  const { data: currentUserRole } = useQuery(getCurrentUserRole, undefined, {
    enabled: Boolean(user),
  });
  const t = useTranslation();
  const isLandingPage = useIsLandingPage();
  const isMarketingPage = useIsMarketingPage();
  const effectiveUser = user
    ? ({ ...user, isAdmin: currentUserRole?.isAdmin ?? Boolean((user as any).isAdmin) } as any)
    : null;

  return (
    <div className="hidden items-center justify-end gap-3 lg:flex lg:flex-1">
      <ul className="flex items-center justify-center gap-2 sm:gap-4">
        <a
          href="https://discord.gg/jnrfKHzUVp"
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Discord"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
          </svg>
        </a>
        <LanguageSwitcher />
        {/* Theme is locked to Classic Dark on marketing pages — hide the toggle there. */}
        {!isMarketingPage && <DarkModeSwitcher restrictToClassic={isLandingPage} />}
      </ul>
      {isMarketingPage && !isUserLoading && !user && (
        <WaspRouterLink to={routes.SignupRoute.to} className="hidden lg:block">
          <BeamButton size="sm" className="px-4 py-1.5 text-sm">
            {t.marketing.nav.ctaStart}
          </BeamButton>
        </WaspRouterLink>
      )}
      {isUserLoading ? null : !user ? (
        <WaspRouterLink
          to={routes.LoginRoute.to}
          className={cn(
            "ml-4 font-semibold leading-6 transition-all duration-300",
            {
              "text-sm": !isScrolled,
              "text-xs": isScrolled,
            },
          )}
        >
          <div className="text-foreground hover:text-primary flex items-center gap-2 rounded-lg px-3 py-1.5 hover:bg-accent transition-all duration-200 ease-in-out">
            <span>{t.nav.login}</span>
            <LogIn
              size={isScrolled ? "1rem" : "1.1rem"}
              className="transition-all duration-300"
            />
          </div>
        </WaspRouterLink>
      ) : effectiveUser ? (
        <div className="ml-3 flex items-center gap-2">
          <NotificationBell />
          <UserDropdown user={effectiveUser} />
        </div>
      ) : null}
    </div>
  );
}

function NavBarMobileMenu({
  isScrolled,
  navigationItems,
  navigate,
}: {
  isScrolled: boolean;
  navigationItems: NavigationItem[];
  navigate: ReturnType<typeof useNavigate>;
}) {
  const { data: user, isLoading: isUserLoading } = useAuth();
  const { data: currentUserRole } = useQuery(getCurrentUserRole, undefined, {
    enabled: Boolean(user),
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const t = useTranslation();
  const isLandingPage = useIsLandingPage();
  const isMarketing = useIsMarketingPage();
  const effectiveUser = user
    ? ({ ...user, isAdmin: currentUserRole?.isAdmin ?? Boolean((user as any).isAdmin) } as any)
    : null;

  return (
    <div className="flex lg:hidden">
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className={cn(
              "text-muted-foreground hover:text-muted hover:bg-accent inline-flex items-center justify-center rounded-md transition-colors",
            )}
          >
            <span className="sr-only">Open main menu</span>
            <Menu
              className={cn("transition-all duration-300", {
                "size-8 p-1": !isScrolled,
                "size-6 p-0.5": isScrolled,
              })}
              aria-hidden="true"
            />
          </button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[300px] sm:w-[400px]">
          <SheetHeader>
            <SheetTitle className="flex items-center">
              <NavBarLogoLink>
                <span className="font-logo font-bold text-xl tracking-tight text-foreground">
                  edgely<span className="text-teal-500">.</span>
                </span>
              </NavBarLogoLink>
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 flow-root">
            <div className="divide-border -my-6 divide-y">
              {isMarketing ? (
                <div className="py-4">
                  <Accordion type="single" collapsible>
                    <AccordionItem value="features">
                      <AccordionTrigger>{t.marketing.nav.features}</AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-1">
                          {getMarketingMegaMenus(t).features.map((e) => (
                            <li key={e.to}>
                              <ReactRouterLink to={e.to} onClick={() => setMobileMenuOpen(false)}
                                className="block rounded-lg px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground">
                                {e.label}
                              </ReactRouterLink>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="solutions">
                      <AccordionTrigger>{t.marketing.nav.forWho}</AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-1">
                          {getMarketingMegaMenus(t).solutions.map((e) => (
                            <li key={e.to}>
                              <ReactRouterLink to={e.to} onClick={() => setMobileMenuOpen(false)}
                                className="block rounded-lg px-3 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground">
                                {e.label}
                              </ReactRouterLink>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                  <ReactRouterLink to="/tarifs" onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground">
                    {t.marketing.nav.pricing}
                  </ReactRouterLink>
                  <ReactRouterLink to="/comparateur" onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground">
                    {t.marketing.nav.comparateur}
                  </ReactRouterLink>
                  <ReactRouterLink to="/blog" onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground">
                    {t.marketing.nav.blog}
                  </ReactRouterLink>
                </div>
              ) : (
                <ul className="space-y-2 py-6">
                  <NavigationItems navigationItems={navigationItems} setMobileMenuOpen={setMobileMenuOpen} navigate={navigate} />
                </ul>
              )}
              <div className="py-6">
                {isUserLoading ? null : !user ? (
                  <WaspRouterLink to={routes.LoginRoute.to}>
                    <div className="text-foreground hover:text-primary flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-accent transition-all duration-200 ease-in-out">
                      <span>{t.nav.login}</span>
                      <LogIn size="1.1rem" />
                    </div>
                  </WaspRouterLink>
                ) : effectiveUser ? (
                  <ul className="space-y-2">
                    <UserMenuItems
                      user={effectiveUser}
                      onItemClick={() => setMobileMenuOpen(false)}
                    />
                  </ul>
                ) : null}
                {isMarketing && !isUserLoading && !user && (
                  <WaspRouterLink to={routes.SignupRoute.to} onClick={() => setMobileMenuOpen(false)} className="block">
                    <BeamButton fullWidth showArrow>{t.marketing.nav.ctaStart}</BeamButton>
                  </WaspRouterLink>
                )}
              </div>
              <div className="py-6 space-y-4">
                <LanguageSwitcher />
                {/* Theme locked to Classic Dark on marketing pages — hide the toggle. */}
                {!isMarketing && <DarkModeSwitcher restrictToClassic={isLandingPage} />}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CustomPageNavLink({ page }: { page: CustomPageNavItem }) {
  const { pathname } = useLocation();
  const isActive = pathname === page.to;
  const [hovered, setHovered] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [value, setValue] = useState(page.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const Icon = ((LucideIcons as any)[page.icon] ?? LayoutDashboard) as React.ComponentType<{ className?: string }>;

  const handleRenameSubmit = async () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== page.name) {
      await page.onRename(trimmed);
    }
    setRenaming(false);
  };

  if (renaming) {
    return (
      <li>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleRenameSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleRenameSubmit();
            if (e.key === "Escape") { setValue(page.name); setRenaming(false); }
          }}
          autoFocus
          className="rounded-lg px-2 py-1 text-sm font-medium border border-primary/40 bg-background outline-none w-32"
        />
      </li>
    );
  }

  return (
    <li
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); if (!showIconPicker) setShowIconPicker(false); }}
    >
      <ReactRouterLink
        to={page.to}
        className={cn(
          "relative flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors duration-200 pr-6",
          isActive ? "text-primary" : "text-foreground/70 hover:text-foreground",
        )}
      >
        {isActive && (
          <motion.span
            layoutId="nav-active-bg"
            className="absolute inset-0 rounded-lg bg-primary/12"
            transition={{ type: "spring", stiffness: 400, damping: 35 }}
          />
        )}
        {/* Icône cliquable */}
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowIconPicker((v) => !v); }}
            className={cn(
              "p-0.5 rounded transition-colors",
              hovered ? "hover:bg-primary/10" : "",
            )}
            title="Changer l'icône"
          >
            <Icon className="h-3.5 w-3.5 relative" />
          </button>
          {showIconPicker && (
            <IconPickerPopover
              currentIcon={page.icon}
              onSelect={(icon) => { page.onChangeIcon(icon); }}
              onClose={() => setShowIconPicker(false)}
            />
          )}
        </div>
        <span className="relative truncate max-w-[100px]">{page.name}</span>
      </ReactRouterLink>
      {hovered && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); setRenaming(true); }}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title="Renommer"
        >
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </li>
  );
}

function NavAddPageItem({ onCreatePage }: { onCreatePage: (name: string, icon: string) => Promise<void> }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await onCreatePage(trimmed, "LayoutDashboard");
    setName("");
    setCreating(false);
  };

  if (creating) {
    return (
      <li className="flex items-center gap-1">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
            if (e.key === "Escape") { setName(""); setCreating(false); }
          }}
          placeholder="Nom..."
          autoFocus
          className="rounded px-2 py-1 text-xs border border-primary/40 bg-background outline-none w-28"
        />
        <button
          type="button"
          onClick={handleSubmit}
          className="p-1 rounded hover:bg-muted text-primary"
        >
          <Check className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={() => { setName(""); setCreating(false); }}
          className="p-1 rounded hover:bg-muted text-muted-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => setCreating(true)}
        className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-muted-foreground/50 hover:text-muted-foreground hover:bg-accent/50 border border-dashed border-border/30 hover:border-border/60 transition-colors"
      >
        <Plus className="h-3 w-3" />
        <span>Ajouter</span>
      </button>
    </li>
  );
}

function MegaMenu({
  label,
  entries,
}: {
  label: string;
  entries: { key: string; label: string; desc: string; to: string }[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <li
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-foreground/70 transition-colors hover:bg-accent hover:text-accent-foreground outline-none"
        aria-expanded={open}
        aria-haspopup="true"
      >
        {label}
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 top-full mt-2 w-[520px] rounded-xl border border-border bg-background/95 p-3 shadow-xl backdrop-blur-lg"
          >
            <div className="grid grid-cols-2 gap-1">
              {entries.map((entry) => (
                <ReactRouterLink
                  key={entry.to}
                  to={entry.to}
                  onClick={() => setOpen(false)}
                  className="group rounded-lg px-3 py-2.5 transition-colors hover:bg-accent"
                >
                  <div className="text-sm font-medium text-foreground group-hover:text-accent-foreground">{entry.label}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground group-hover:text-accent-foreground/80">{entry.desc}</div>
                </ReactRouterLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

function MarketingDesktopNav() {
  const t = useTranslation();
  const menus = getMarketingMegaMenus(t);
  return (
    <>
      <MegaMenu label={t.marketing.nav.features} entries={menus.features} />
      <MegaMenu label={t.marketing.nav.forWho} entries={menus.solutions} />
      <li>
        <ReactRouterLink
          to="/tarifs"
          className="flex items-center rounded-lg px-3 py-1.5 text-sm font-medium text-foreground/70 transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          {t.marketing.nav.pricing}
        </ReactRouterLink>
      </li>
      <li>
        <ReactRouterLink
          to="/comparateur"
          className="flex items-center rounded-lg px-3 py-1.5 text-sm font-medium text-foreground/70 transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          {t.marketing.nav.comparateur}
        </ReactRouterLink>
      </li>
      <li>
        <ReactRouterLink
          to="/blog"
          className="flex items-center rounded-lg px-3 py-1.5 text-sm font-medium text-foreground/70 transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          {t.marketing.nav.blog}
        </ReactRouterLink>
      </li>
    </>
  );
}

function NavigationItems({
  navigationItems,
  setMobileMenuOpen,
  navigate,
  customPages,
}: {
  navigationItems: NavigationItem[];
  setMobileMenuOpen?: Dispatch<SetStateAction<boolean>>;
  navigate?: ReturnType<typeof useNavigate>;
  customPages?: CustomPagesActions;
}) {
  const { pathname } = useLocation();
  const t = useTranslation();
  const isMobile = !!setMobileMenuOpen;

  const primaryRoutes = ['/dashboard', '/trading-journal', '/trading-analytics', '/trading-sessions', '/trading-accounts', '/prop-firm-manager', '/blog'];
  const primaryItems = navigationItems.filter(item =>
    primaryRoutes.some(r => item.to === r || item.to.startsWith(r))
  );
  const secondaryItems = navigationItems.filter(item =>
    !primaryRoutes.some(r => item.to === r || item.to.startsWith(r)) &&
    !item.to.startsWith('#')
  );
  const anchorItems = navigationItems.filter(item => item.to.startsWith('#'));

  if (isMobile) {
    // Mode mobile : afficher tous les items sans regroupement
    return (
      <>
        {navigationItems.map((item) => {
          const isAnchorLink = item.to.startsWith("#");
          const anchorId = isAnchorLink ? item.to.replace("#", "") : null;
          const Icon = item.icon;
          const isActive = !isAnchorLink && pathname.startsWith(item.to);

          const mobileStyles = cn(
            "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium leading-7 transition-all duration-200",
            isActive
              ? "bg-primary/15 text-primary"
              : "text-foreground hover:bg-accent hover:text-foreground",
          );

          return (
            <li key={item.name}>
              {isAnchorLink ? (
                <a
                  href={item.to}
                  className={mobileStyles}
                  onClick={(e) => {
                    handleAnchorClick(e, anchorId!, navigate);
                    if (setMobileMenuOpen) setMobileMenuOpen(false);
                  }}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  <span>{item.name}</span>
                </a>
              ) : (
                <ReactRouterLink
                  to={item.to}
                  className={mobileStyles}
                  onClick={() => setMobileMenuOpen(false)}
                  target={item.to.startsWith("http") ? "_blank" : undefined}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  <span>{item.name}</span>
                  {isActive && (
                    <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                  )}
                </ReactRouterLink>
              )}
            </li>
          );
        })}
      </>
    );
  }

  // Mode desktop : respecte l'ordre du tableau, ancres et liens mélangés
  // Les items non-primaires et non-ancres vont dans le dropdown "Outils"
  return (
    <>
      {navigationItems.map((item) => {
        const Icon = item.icon;
        const isAnchor = item.to.startsWith('#');
        const isPrimary = primaryRoutes.some(r => item.to === r || item.to.startsWith(r));
        const isSecondary = !isPrimary && !isAnchor;

        if (isSecondary) return null; // rendu séparé via dropdown ci-dessous

        const isActive = !isAnchor && pathname.startsWith(item.to);

        if (isAnchor) {
          const anchorId = item.to.replace('#', '');
          return (
            <li key={item.name}>
              <a
                href={item.to}
                className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200 text-foreground/70 hover:bg-accent hover:text-foreground"
                onClick={(e) => handleAnchorClick(e, anchorId, navigate)}
              >
                {Icon && <Icon className="h-4 w-4" />}
                <span>{item.name}</span>
              </a>
            </li>
          );
        }

        return (
          <li key={item.name}>
            <ReactRouterLink
              to={item.to}
              className={cn(
                "relative flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors duration-200",
                isActive ? "text-primary" : "text-foreground/70 hover:text-foreground",
              )}
            >
              {isActive && (
                <motion.span
                  layoutId="nav-active-bg"
                  className="absolute inset-0 rounded-lg bg-primary/12"
                  transition={{ type: "spring", stiffness: 400, damping: 35 }}
                />
              )}
              <motion.span
                className="absolute inset-0 rounded-lg bg-accent opacity-0"
                whileHover={{ opacity: isActive ? 0 : 1 }}
                transition={{ duration: 0.15 }}
              />
              {Icon && <Icon className="relative h-4 w-4" />}
              <span className="relative">{item.name}</span>
            </ReactRouterLink>
          </li>
        );
      })}

      {/* Dropdown "Outils" pour les items secondaires */}
      {secondaryItems.length > 0 && (
        <li>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-foreground/70 transition-all duration-200 hover:bg-accent hover:text-foreground outline-none">
              {t.nav.tools}
              <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[160px]">
              {secondaryItems.map((item) => {
                const Icon = item.icon;
                const isActive = pathname.startsWith(item.to);
                return (
                  <DropdownMenuItem key={item.name} asChild>
                    <ReactRouterLink
                      to={item.to}
                      className={cn(
                        "flex items-center gap-2 cursor-pointer",
                        isActive && "text-primary"
                      )}
                    >
                      {Icon && <Icon className="h-4 w-4" />}
                      {item.name}
                    </ReactRouterLink>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </li>
      )}

      {/* Séparateur + pages custom */}
      {customPages && (
        <>
          <li className="flex items-center px-1">
            <span className="h-4 w-px bg-border/60" />
          </li>
          {customPages.items.map((page) => (
            <CustomPageNavLink key={page.id} page={page} />
          ))}
          <NavAddPageItem onCreatePage={customPages.onCreatePage} />
        </>
      )}
    </>
  );
}

function NavBarLogo({ isScrolled }: { isScrolled: boolean }) {
  const { data: user } = useAuth();
  const logoLink = user ? routes.TradingDashboardRoute.to : routes.LandingPageRoute.to;

  return (
    <WaspRouterLink
      to={logoLink}
      className="text-foreground hover:text-primary flex items-center transition-all duration-300 ease-in-out"
    >
      <span
        className={cn(
          "font-display font-bold tracking-tight transition-all duration-300",
          {
            "text-xl": !isScrolled,
            "text-lg": isScrolled,
          },
        )}
      >
        edgely<span className="text-teal-500">.</span>
      </span>
    </WaspRouterLink>
  );
}

function NavBarLogoLink({ children }: { children: ReactNode }) {
  const { data: user } = useAuth();
  const logoLink = user ? routes.TradingDashboardRoute.to : routes.LandingPageRoute.to;

  return (
    <WaspRouterLink to={logoLink}>
      {children}
    </WaspRouterLink>
  );
}

